import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { ThreeDotsHorizontalIcon } from "@/icons";
import { useClickOutside } from "@/interface/hooks";
import { BaseInput } from "@/interface/ui/inputs/BaseInput";
import { decodeHtml } from "@/shared/lib/decodeHtml";
import { getDate } from "@/utils";
import type { PresentationData } from "../types";
import { ContextMenu } from "./ContextMenu";
import styles from "./PresentationsList.module.scss";

type PresentationsListItemProps = {
    data: PresentationData;
    onSelect: (data: PresentationData) => void;
    onDelete: (data: PresentationData) => void;
    onRename: (UID: string, newName: string) => void;
    isMenuOpen: boolean;
    onMenuOpen: () => void;
    onMenuClose: () => void;
    isEditorOrOwner: boolean;
};

export const PresentationsListItem = ({
    data,
    onSelect,
    onDelete,
    onRename,
    isMenuOpen,
    onMenuOpen,
    onMenuClose,
    isEditorOrOwner,
}: PresentationsListItemProps) => {
    const { t } = useTranslation();
    const menuRef = useRef<HTMLDivElement | null>(null);
    useClickOutside(menuRef, onMenuClose);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(decodeHtml(data.title));

    const inputRef = useRef<HTMLInputElement | null>(null);

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const startEdit = () => {
        setValue(data.title);
        setIsEditing(true);
        closeMenu();
    };

    const stopEdit = () => {
        setIsEditing(false);
        if (data.title !== value && value.length) {
            onRename(data.UID, value);
        }
        canv.appInterface.inputFocused = 0;
    };

    const cancelEdit = () => {
        setValue(data.title);
        setIsEditing(false);
    };

    const deletePresentation = () => {
        onDelete(data);
        closeMenu();
    };

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
        }
    }, [isEditing]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            stopEdit();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            cancelEdit();
        }
    };

    const handleMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        onMenuOpen();
    };

    const handleFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    const handleBlur = () => {
        canv.appInterface.inputFocused = 0;
        stopEdit();
    };

    return (
        <>
            {isEditing ? (
                <div className={styles.inputWrapper}>
                    <BaseInput
                        id={`${data.UID}-baseInput`}
                        value={value}
                        label={t("presentations.renameTitle")}
                        inputRef={inputRef}
                        onChange={setValue}
                        onKeyDown={handleKeyDown}
                        onCancel={cancelEdit}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                </div>
            ) : (
                <button
                    type="button"
                    className={styles.slidesListItem}
                    onClick={() => onSelect(data)}
                >
                    <div className={styles.slidesListItemTitleWrapper}>
                        <p>{decodeHtml(data.title)}</p>
                        {isEditorOrOwner && (
                            <div className={styles.moreActionsButton} onClick={handleMenuClick}>
                                <ThreeDotsHorizontalIcon />
                            </div>
                        )}
                    </div>

                    <div className={styles.slidesListItemTimeAndActor}>
                        <p>{`${getDate(data.date)} ${data.actor}`}</p>
                    </div>
                </button>
            )}

            {isMenuOpen && anchorEl && (
                <ContextMenu
                    ref={menuRef}
                    anchorEl={anchorEl}
                    onEdit={startEdit}
                    onDelete={deletePresentation}
                />
            )}
        </>
    );
};
